import React, { useState } from 'react';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import FormField from '../../components/common/FormField';
import { ClientDTO } from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import {
  useFormValidation,
  ValidationRules,
} from '../../hooks/useFormValidation';

interface ClientFormProps {
  initialValues: ClientDTO;
  onSubmit: (data: ClientDTO) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const FORM_SECTIONS = {
  basicInfo: {
    id: 'basic-info',
    title: 'Basic Information',
  },
  addressInfo: {
    id: 'address-info',
    title: 'Address Information',
  },
  contactInfo: {
    id: 'contact-info',
    title: 'Contact Information',
  },
} as const;

const ClientForm: React.FC<ClientFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<ClientDTO>(initialValues);

  const validationRules: ValidationRules = {
    name: [{ required: true, message: 'Name is required' }],
    defaultHourlyRate: [
      { required: true, message: 'Default Hourly Rate is required' },
      {
        custom: (value) => Number(value) >= 0,
        message: 'Default Hourly Rate must be a positive number',
      },
    ],
  };

  const { errors, validate } = useFormValidation<ClientDTO>(validationRules);

  const handleSave = () => {
    if (validate(formData)) {
      onSubmit(formData);
    }
  };

  const sections = Object.values(FORM_SECTIONS);

  return (
    <Form
      sections={sections}
      onSave={handleSave}
      onCancel={onCancel}
      isSubmitting={isLoading}
    >
      <FormSection
        id={FORM_SECTIONS.basicInfo.id}
        title={FORM_SECTIONS.basicInfo.title}
      >
        <FormField
          id="name"
          label="Name"
          type="text"
          value={formData.name || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Name"
          required={true}
          error={errors.name}
        />
        <FormField
          id="vatNumber"
          label="VAT Number"
          type="text"
          value={formData.vatNumber || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter VAT Number"
          required={false}
          error={errors.vatNumber}
        />
        <FormField
          id="defaultHourlyRate"
          label="Default Hourly Rate"
          type="number"
          value={formData.defaultHourlyRate || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Default Hourly Rate"
          required={true}
          error={errors.defaultHourlyRate}
        />
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.addressInfo.id}
        title={FORM_SECTIONS.addressInfo.title}
      >
        <FormField
          id="streetAddress"
          label="Street Address"
          type="text"
          value={formData.streetAddress || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Street Address"
          required={false}
          error={errors.streetAddress}
        />
        <FormField
          id="suburb"
          label="Suburb"
          type="text"
          value={formData.suburb || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Suburb"
          required={false}
          error={errors.suburb}
        />
        <FormField
          id="city"
          label="City"
          type="text"
          value={formData.city || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter City"
          required={false}
          error={errors.city}
        />
        <FormField
          id="province"
          label="Province"
          type="text"
          value={formData.province || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Province"
          required={false}
          error={errors.province}
        />
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.contactInfo.id}
        title={FORM_SECTIONS.contactInfo.title}
      >
        <FormField
          id="contactPersonName"
          label="Contact Person Name"
          type="text"
          value={formData.contactPersonName || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Contact Person Name"
          required={false}
          error={errors.contactPersonName}
        />
        <FormField
          id="contactPersonEmail"
          label="Contact Person Email"
          type="text"
          value={formData.contactPersonEmail || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Contact Person Email"
          required={false}
          error={errors.contactPersonEmail}
        />
        <FormField
          id="contactPersonPhone"
          label="Contact Person Phone"
          type="text"
          value={formData.contactPersonPhone || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Contact Person Phone"
          required={false}
          error={errors.contactPersonPhone}
        />
      </FormSection>
    </Form>
  );
};

export default ClientForm;
